import React, { useEffect, useMemo, useState } from "react";
import {
    DndContext,
    PointerSensor,
    TouchSensor,
    closestCenter,
    useSensor,
    useSensors,
} from "@dnd-kit/core";
import { SortableContext, arrayMove, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { restrictToVerticalAxis } from "@dnd-kit/modifiers";
import PortfolioPackageRow from "./PortfolioPackageRow";
import PortfolioPackageEditor from "./PortfolioPackageEditor";
import {
    fetchPortfolioPackages,
    upsertPortfolioPackage,
    reorderPortfolioPackages,
    deletePortfolioPackage,
    seedPortfolioPackages,
} from "../../services/portfolioPackageService";
import { isSupabaseConfigured } from "../../lib/supabaseClient";

export default function PortfolioPackageManager() {
    const [packages, setPackages] = useState([]);
    const [selectedId, setSelectedId] = useState(null);
    const [draft, setDraft] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [reorderMessage, setReorderMessage] = useState("");
    const [formNotice, setFormNotice] = useState("");
    const [isSaving, setIsSaving] = useState(false);
    const [hasDraftChanges, setHasDraftChanges] = useState(false);
    const [searchTerm, setSearchTerm] = useState("");
    const [supabaseNotice, setSupabaseNotice] = useState("");
    const [isSeeding, setIsSeeding] = useState(false);

    const sensors = useSensors(
        useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
        useSensor(TouchSensor)
    );

    useEffect(() => {
        const loadData = async () => {
            try {
                setIsLoading(true);
                const data = await fetchPortfolioPackages();
                const sorted = [...data].sort((a, b) => (a.orderIndex ?? 0) - (b.orderIndex ?? 0));
                setPackages(sorted);
                setSelectedId((prev) => prev ?? sorted[0]?.id ?? null);
                if (isSupabaseConfigured) {
                    setSupabaseNotice(sorted.length ? "Supabase bağlantısı aktif." : "Supabase tablosu boş. Demo verilerini içe aktarmak için butonu kullanın.");
                } else {
                    setSupabaseNotice("Supabase anahtarları tanımlı değil; değişiklikler bu oturumda kalır.");
                }
            } catch (fetchError) {
                console.error(fetchError);
                setError("Portfolyo paketleri yüklenemedi.");
                if (isSupabaseConfigured) {
                    setSupabaseNotice("Supabase bağlantısında sorun oluştu. Ayarlarınızı kontrol edin.");
                }
            } finally {
                setIsLoading(false);
            }
        };

        loadData();
    }, []);

    useEffect(() => {
        if (!selectedId && packages.length) {
            setSelectedId(packages[0].id);
        }
    }, [packages, selectedId]);

    useEffect(() => {
        if (!selectedId) {
            setDraft(null);
            return;
        }
        const original = packages.find((item) => item.id === selectedId);
        if (original) {
            setDraft(clonePackage(original));
            setHasDraftChanges(false);
        }
    }, [packages, selectedId]);

    const handleDragEnd = (event) => {
        const { active, over } = event;
        if (!over || active.id === over.id) {
            return;
        }

        setPackages((items) => {
            const oldIndex = items.findIndex((item) => item.id === active.id);
            const newIndex = items.findIndex((item) => item.id === over.id);
            const reordered = arrayMove(items, oldIndex, newIndex).map((item, index) => ({
                ...item,
                orderIndex: index,
            }));
            queueMicrotask(() => persistReorder(reordered));
            return reordered;
        });
    };

    const persistReorder = async (nextOrder) => {
        if (!isSupabaseConfigured) {
            setReorderMessage("Sıralama Supabase bağlı olduğunda kaydedilir. Şimdilik sadece önizleme değişti.");
            return;
        }

        try {
            setReorderMessage("Sıralama kaydediliyor...");
            await reorderPortfolioPackages(nextOrder.map((item) => item.id));
            setReorderMessage("Sıralama güncellendi.");
        } catch (persistError) {
            console.error(persistError);
            setReorderMessage("Sıralama kaydedilemedi. Tekrar deneyin.");
        }
    };

    const handleSeedSupabase = async () => {
        if (!isSupabaseConfigured) {
            setSupabaseNotice("Supabase yapılandırması bulunamadı.");
            return;
        }

        setIsSeeding(true);
        setSupabaseNotice("Demo verileri Supabase'e aktarılıyor...");
        try {
            const seeded = await seedPortfolioPackages();
            setPackages(seeded);
            setSelectedId((prev) => prev ?? seeded[0]?.id ?? null);
            setSupabaseNotice("Supabase portfolyo paketleri güncellendi.");
        } catch (seedError) {
            console.error(seedError);
            setSupabaseNotice("Supabase'e veri aktarılırken hata oluştu.");
        } finally {
            setIsSeeding(false);
        }
    };

    const infoMessage = useMemo(() => {
        if (isLoading) {
            return "Portfolyo paketleri yükleniyor...";
        }
        if (error) {
            return error;
        }
        if (!packages.length) {
            return "Henüz portfolyo paketi eklenmemiş.";
        }
        return "";
    }, [isLoading, error, packages]);

    const filteredPackages = useMemo(() => {
        if (!searchTerm.trim()) {
            return packages;
        }
        const query = searchTerm.trim().toLowerCase();
        return packages.filter((item) =>
            [item.title, item.cardTitle, item.cardSummary]
                .filter(Boolean)
                .some((value) => value.toLowerCase().includes(query))
        );
    }, [packages, searchTerm]);

    const handleSelect = (id) => {
        setSelectedId(id);
        setFormNotice("");
    };

    const handleDraftChange = (nextDraft) => {
        setDraft(nextDraft);
        setHasDraftChanges(true);
        setFormNotice("");
    };

    const handleResetDraft = () => {
        if (!selectedId) {
            return;
        }
        const original = packages.find((item) => item.id === selectedId);
        if (original) {
            setDraft(clonePackage(original));
            setHasDraftChanges(false);
            setFormNotice("Değişiklikler sıfırlandı.");
        }
    };

    const handleCreate = () => {
        const newEntry = createBlankPackage(packages.length);
        setPackages((items) => {
            const next = [newEntry, ...items].map((item, index) => ({ ...item, orderIndex: index }));
            return next;
        });
        setSelectedId(newEntry.id);
        setDraft(newEntry);
        setHasDraftChanges(true);
        setFormNotice("Yeni portfolyo paketi oluşturuldu.");
    };

    const commitToState = (savedPackage) => {
        setPackages((items) => {
            const exists = items.some((item) => item.id === savedPackage.id);
            if (exists) {
                return items.map((item) => (item.id === savedPackage.id ? savedPackage : item));
            }
            const next = [savedPackage, ...items];
            return next.map((item, index) => ({ ...item, orderIndex: index }));
        });
        setDraft(clonePackage(savedPackage));
        setSelectedId(savedPackage.id);
        setHasDraftChanges(false);
    };

    const handleSave = async () => {
        if (!draft) {
            return;
        }
        setIsSaving(true);
        setFormNotice("");
        const sanitized = sanitizePackage(draft);

        try {
            if (!isSupabaseConfigured) {
                commitToState(sanitized);
                setFormNotice("Supabase tanımlı olmadığı için değişiklikler bu oturumda tutuluyor.");
                return;
            }

            const saved = await upsertPortfolioPackage(sanitized);
            commitToState(saved);
            setFormNotice("Portfolyo paketi kaydedildi.");
        } catch (persistError) {
            console.error(persistError);
            setFormNotice("Kaydetme sırasında hata oluştu.");
        } finally {
            setIsSaving(false);
        }
    };

    const handleDelete = async (id) => {
        if (!id) {
            return;
        }

        try {
            if (isSupabaseConfigured) {
                await deletePortfolioPackage(id);
            }
            setPackages((items) => items.filter((item) => item.id !== id));
            setFormNotice(isSupabaseConfigured ? "Portfolyo paketi silindi." : "Taslak silindi (Supabase bağlı değil).");

            if (selectedId === id) {
                const fallback = packages.find((item) => item.id !== id);
                setSelectedId(fallback?.id ?? null);
                setDraft(fallback ? clonePackage(fallback) : null);
                setHasDraftChanges(false);
            }
        } catch (deleteError) {
            console.error(deleteError);
            setFormNotice("Silme sırasında hata oluştu.");
        }
    };

    const handleDuplicate = () => {
        if (!draft) {
            return;
        }
        const duplicated = clonePackage(draft);
        duplicated.id = generateId();
        duplicated.slug = `${duplicated.slug || "paket"}-${Math.random().toString(36).slice(2, 6)}`;
        duplicated.title = `${duplicated.title || "Yeni paket"} (Kopya)`;
        duplicated.published = false;
        duplicated.orderIndex = 0;

        setPackages((items) => {
            const next = [duplicated, ...items].map((item, index) => ({ ...item, orderIndex: index }));
            return next;
        });
        setSelectedId(duplicated.id);
        setDraft(duplicated);
        setHasDraftChanges(true);
        setFormNotice("Taslak kopyalandı. Kaydetmek için değişiklikleri kaydet butonuna basın.");
    };

    const handleTogglePublished = async (id) => {
        const target = packages.find((item) => item.id === id);
        if (!target) {
            return;
        }
        const nextState = { ...target, published: !target.published };

        if (!isSupabaseConfigured) {
            commitToState(nextState);
            setFormNotice("Supabase bağlı olmadığı için yayın durumu bu oturumda değiştirildi.");
            return;
        }

        setIsSaving(true);
        try {
            const saved = await upsertPortfolioPackage(nextState);
            commitToState(saved);
            setFormNotice(saved.published ? "Paket yayına alındı." : "Paket taslak yapıldı.");
        } catch (toggleError) {
            console.error(toggleError);
            setFormNotice("Yayın durumu güncellenemedi.");
        } finally {
            setIsSaving(false);
        }
    };

    const isFiltering = Boolean(searchTerm.trim());
    const listForRender = filteredPackages;

    return (
        <section className="package-manager">
            <header className="package-manager__toolbar">
                <div>
                    <h2 className="admin-shell__title" style={{ fontSize: "1.4rem" }}>Portfolyo Paketleri</h2>
                    <p className="admin-shell__subtitle" style={{ maxWidth: "760px" }}>
                        Ana portfolyo sayfasındaki modal paketleri ve hero kartlarını burada yönetin. Supabase etkinleştirildiğinde değişiklikler otomatik kaydedilir.
                    </p>
                </div>
                <div className="package-manager__actions">
                    {isSupabaseConfigured ? (
                        <button
                            type="button"
                            className="signature-manager__button signature-manager__button--ghost"
                            onClick={handleSeedSupabase}
                            disabled={isSeeding}
                        >
                            {isSeeding ? "Supabase senkronize ediliyor..." : "Supabase verisini senkronize et"}
                        </button>
                    ) : (
                        <span className="signature-manager__status">Supabase pasif</span>
                    )}
                    <div className="package-manager__search">
                        <input
                            type="search"
                            value={searchTerm}
                            onChange={(event) => setSearchTerm(event.target.value)}
                            placeholder="Paketlerde ara..."
                        />
                    </div>
                    <button type="button" className="signature-manager__button" onClick={handleCreate}>
                        Yeni paket
                    </button>
                </div>
            </header>

            {infoMessage && <p style={{ color: "rgba(224, 229, 245, 0.68)" }}>{infoMessage}</p>}

            {!!reorderMessage && (
                <p style={{ color: "rgba(200, 167, 232, 0.75)", fontSize: "0.85rem", letterSpacing: "0.18rem" }}>{reorderMessage}</p>
            )}

            {!!supabaseNotice && (
                <p className="signature-manager__flash" style={{ borderColor: "rgba(200, 167, 232, 0.35)" }}>{supabaseNotice}</p>
            )}

            <div className="package-manager__workspace">
                <div className="package-manager__catalog" aria-label="Portfolyo paket listesi">
                    {isFiltering && !listForRender.length ? (
                        <div className="signature-manager__empty">Arama sonucunda paket bulunamadı.</div>
                    ) : null}

                    {isFiltering ? (
                        <div className="package-manager__list">
                            {listForRender.map((item) => (
                                <PortfolioPackageRow
                                    key={item.id}
                                    item={item}
                                    isActive={item.id === selectedId}
                                    onSelect={handleSelect}
                                    onTogglePublished={handleTogglePublished}
                                    sortableDisabled
                                />
                            ))}
                        </div>
                    ) : (
                        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd} modifiers={[restrictToVerticalAxis]}>
                            <SortableContext items={packages.map((item) => item.id)} strategy={verticalListSortingStrategy}>
                                <div className="package-manager__list">
                                    {packages.map((item) => (
                                        <PortfolioPackageRow
                                            key={item.id}
                                            item={item}
                                            isActive={item.id === selectedId}
                                            onSelect={handleSelect}
                                            onTogglePublished={handleTogglePublished}
                                        />
                                    ))}
                                </div>
                            </SortableContext>
                        </DndContext>
                    )}
                </div>

                <PortfolioPackageEditor
                    draft={draft}
                    onChange={handleDraftChange}
                    onSave={handleSave}
                    onReset={handleResetDraft}
                    onDelete={handleDelete}
                    onDuplicate={handleDuplicate}
                    saving={isSaving}
                    hasChanges={hasDraftChanges}
                    supabaseEnabled={isSupabaseConfigured}
                />
            </div>

            {!!formNotice && <p className="signature-manager__flash">{formNotice}</p>}
        </section>
    );
}

const clonePackage = (payload) => JSON.parse(JSON.stringify(payload));

const generateId = () => {
    if (typeof crypto !== "undefined" && crypto.randomUUID) {
        return crypto.randomUUID();
    }
    return `package-${Math.random().toString(36).slice(2, 10)}`;
};

const createBlankPackage = (orderIndex) => ({
    id: generateId(),
    slug: "",
    cardTitle: "YENI PAKET",
    cardSummary: "Hero kartında görünecek kısa açıklama",
    title: "",
    meta: [""],
    body: [""],
    ctaLabel: "Brief iste",
    ctaUrl: "mailto:studio@modelerman.com",
    orderIndex,
    published: false,
});

const sanitizePackage = (payload) => {
    const next = clonePackage(payload);
    next.meta = (next.meta ?? []).filter((entry) => entry?.trim());
    next.body = (next.body ?? []).filter((entry) => entry?.trim());
    next.slug = next.slug?.trim() || next.id;
    next.cardTitle = next.cardTitle?.trim() || next.title?.toUpperCase() || "PAKET";
    next.cardSummary = next.cardSummary?.trim() || "";
    next.title = next.title?.trim() || next.cardTitle;
    next.ctaLabel = next.ctaLabel?.trim() || "Brief iste";
    next.ctaUrl = next.ctaUrl?.trim() || "mailto:studio@modelerman.com";
    return next;
};
